import {
	Button,
	Typography,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Tooltip
} from '@mui/material';
import { motion } from 'motion/react';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import useThemeMediaQuery from '@fuse/hooks/useThemeMediaQuery';
import PageBreadcrumb from 'src/components/PageBreadcrumb';
import useUser from '@auth/useUser';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsApplicationsLoading } from '@/components/theme-layouts/components/navbar/navbarSlice';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { useGetAddressesListQuery } from '@/store/services/address';
import useGetCurrentPlan from '@/pages/apps/billing/utilities/hooks/useGetCurrentPlan';
import { useGetDashboardStatsQuery } from '@/store/services/reports';

/**
 * The orders header.
 */
function ApplicationsHeader() {
	const { data: user } = useUser();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const isApplicationsLoading = useSelector(selectIsApplicationsLoading);
	const [isNavigationLoading, setIsNavigationLoading] = useState(false);
	const [noAddressDialogOpen, setNoAddressDialogOpen] = useState(false);
	const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
	const { data: addressesResponse } = useGetAddressesListQuery({});
	const addresses = addressesResponse?.data || [];

	// Check if all addresses have an application (are disabled)
	const allAddressesDisabled = useMemo(() => {
		if (addresses.length === 0) return false;
		return addresses.every((address: any) => address.application !== null);
	}, [addresses]);

   const { currentPlanInfo,isLoading:isLoadingCurrnetPlan } = useGetCurrentPlan();
    const { data: dashboardStats, isFetching } = useGetDashboardStatsQuery(
	   undefined,
	   {
		 refetchOnMountOrArgChange: true,
	   }
	 );

	  const shouldDisabledAssetButton = useMemo(() => {
		const totalAssets = dashboardStats?.data?.total_applications_assets_count;
		const allowedApplications = currentPlanInfo?.number_of_applications;
	
		return (
		  (totalAssets === 2 && allowedApplications === 2) ||
		  (totalAssets === 1 && allowedApplications === 1)
		);
	  }, [
		dashboardStats?.data?.total_applications_assets_count,
		currentPlanInfo?.number_of_applications,
	  ]);

	const handleNewApplicationClick = (e: React.MouseEvent) => {
		e.preventDefault();

		if (addresses.length === 0) {
			setNoAddressDialogOpen(true);
			return;
		}

		setIsNavigationLoading(true);
		navigate('/apps/applications/new');
	};

	const planLimitTooltip =
		currentPlanInfo != null
			? `Plan limit reached — You have reached the maximum number of addresses allowed in your current plan (${currentPlanInfo.number_of_applications}). One application is allowed per address.`
			: 'Plan limit reached';

	const addNewAssetDisabled =
		isLoadingCurrnetPlan || isNavigationLoading || shouldDisabledAssetButton;

	const addNewAssetButton = (
		<Button
			className=""
			variant="contained"
			color="secondary"
			onClick={handleNewApplicationClick}
			disabled={addNewAssetDisabled}
			size={isMobile ? 'small' : 'medium'}
		>
			{isNavigationLoading ? (
				<CircularProgress
					size={20}
					color="inherit"
				/>
			) : (
				<FuseSvgIcon size={20}>heroicons-outline:plus</FuseSvgIcon>
			)}
			<span className="mx-1 sm:mx-2">Add New Asset</span>
		</Button>
	);

	useEffect(() => {
		// Reset loading state when component unmounts or route changes
		return () => {
			setIsNavigationLoading(false);
		};
	}, [dispatch]);



	return (
		<>
			<Dialog
				open={noAddressDialogOpen}
				onClose={() => setNoAddressDialogOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>No address found</DialogTitle>
				<DialogContent>
					<Typography className="text-gray-700">
						Currently there is no address. First add an address then you can add a new asset.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setNoAddressDialogOpen(false)} disabled={isNavigationLoading}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="secondary"
						onClick={() => {
							setNoAddressDialogOpen(false);
							navigate('/apps/addresses');
						}}
					>
						Add Address
					</Button>
				</DialogActions>
			</Dialog>

			<div className="flex grow-0 flex-1 w-full items-end justify-between space-y-2 sm:space-y-0 py-6 sm:py-8">
			<motion.span
				initial={{ x: -20 }}
				animate={{ x: 0, transition: { delay: 0.2 } }}
			>
				<div>
					<PageBreadcrumb className="mb-2" />
					<Typography className="text-4xl font-bold leading-none tracking-tight">My Assets</Typography>
				</div>
			</motion.span>
			{user?.role === 'Applicant' && !isApplicationsLoading && (
				<div className="flex flex-1 items-center justify-end space-x-2 mb-2 sm:mb-0">
					<motion.div
						className="flex grow-0"
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0, transition: { delay: 0.2 } }}
					>
						{shouldDisabledAssetButton ? (
							<Tooltip title={planLimitTooltip} arrow placement="top">
								<span className="inline-flex">{addNewAssetButton}</span>
							</Tooltip>
						) : (
							addNewAssetButton
						)}
					</motion.div>
				</div>
			)}
			</div>
		</>
	);
}

export default ApplicationsHeader;
